import { cloneElement, isValidElement, useId } from 'react';
import type { JSX, ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/cn.js';
import { Label } from './label.js';

export interface FieldProps {
  label: ReactNode;
  /** `id` du contrôle, pour relier le libellé (`htmlFor`). */
  htmlFor?: string | undefined;
  /** Aide courte sous le champ (12 px). */
  hint?: ReactNode;
  /** Message d'erreur de validation, affiché sous l'aide (qui reste visible). */
  error?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}

interface DescribableProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Champ de formulaire : libellé au-dessus, contrôle, puis aide et erreur.
 * Même rythme vertical partout (libellé → contrôle 6 px, contrôle → aide
 * 6 px) ; dans une grille, les contrôles restent alignés d'une colonne à
 * l'autre.
 *
 * Quand l'enfant direct est le contrôle désigné par `htmlFor`, l'aide et
 * l'erreur lui sont reliées (`aria-describedby`) et l'erreur le marque
 * `aria-invalid`. Pour un contrôle composite, utiliser `fieldMessageIds`.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  className,
}: FieldProps): JSX.Element {
  const fallbackId = useId();
  const ids = fieldMessageIds(htmlFor ?? fallbackId);
  const describedBy = [hint ? ids.hint : null, error ? ids.error : null].filter((id) => id !== null).join(' ');

  let control: ReactNode = children;
  if (htmlFor !== undefined && isValidElement<DescribableProps>(children) && children.props.id === htmlFor) {
    const element: ReactElement<DescribableProps> = children;
    const existing = element.props['aria-describedby'];
    const merged = [existing, describedBy].filter((value) => value !== undefined && value !== '').join(' ');
    control = cloneElement(element, {
      ...(merged === '' ? {} : { 'aria-describedby': merged }),
      ...(error ? { 'aria-invalid': true } : {}),
    });
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {control}
      {hint ? (
        <p id={ids.hint} className="text-xs leading-5 text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={ids.error} className="text-xs text-status-red-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Identifiants de l'aide et de l'erreur d'un champ, pour les relier à un contrôle composite. */
export function fieldMessageIds(controlId: string): { hint: string; error: string } {
  return { hint: `${controlId}-hint`, error: `${controlId}-error` };
}
